import { useEffect, useRef } from "react";
import { Gauge, Moon, RotateCcw, RotateCw } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookmarkButton } from "@/components/Bookmarks";
import { usePlayer } from "@/context/PlayerContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const SPEEDS = [0.75, 1, 1.25, 1.5, 1.75, 2];
const SLEEPS = [15, 30, 45, 60];
const fmtLeft = (until) => { const m = Math.max(0, Math.round((until - Date.now()) / 60000)); return m < 1 ? "<1m" : `${m}m`; };

/* Saves listening position for the playing audiobook every ~10 s while playing, and once on pause. Never writes for a merely-restored (paused) player. */
export const AudiobookSync = () => {
  const p = usePlayer();
  const t = p.current;
  const last = useRef({ id: null, at: 0 });
  const played = useRef(false);
  useEffect(() => {
    if (!t?.is_audiobook || !t.album_id || !p.playing) return;
    played.current = true;
    const now = Date.now();
    const changed = last.current.id !== t.id;
    if (!changed && now - last.current.at < 10000) return;
    if (p.time < 1) return;
    last.current = { id: t.id, at: now };
    api.put(`/audiobooks/${t.album_id}/progress`, { track_id: t.id, position: p.time }).catch(() => {});
  }, [t, p.time, p.playing]);
  useEffect(() => {
    if (!t?.is_audiobook || p.playing || !played.current) return;
    if (p.time >= 1) api.put(`/audiobooks/${t.album_id}/progress`, { track_id: t.id, position: p.time }).catch(() => {});
  }, [p.playing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onDone = async (e) => {
      const { album_id, track_id, duration } = e.detail || {};
      if (!album_id) return;
      try {
        await api.put(`/audiobooks/${album_id}/progress`, { track_id, position: duration || 0, finished: true });
        const { data } = await api.get(`/audiobooks/${album_id}/next`);
        const nb = data.book;
        toast.success("Book finished", {
          description: nb ? `Up next for you: ${nb.name} — ${nb.artist}` : "Nothing else waiting on your shelf.",
          duration: 12000,
          action: nb ? { label: "Start", onClick: () => { if (p.shuffle) p.setShuffle(false); const idx = Math.max(0, (nb.tracks || []).findIndex((x) => x.id === nb.progress?.track_id)); p.playTracks(nb.tracks, idx, nb.progress?.position || 0); } } : undefined,
        });
      } catch { /* progress endpoints already toast on the page */ }
    };
    window.addEventListener("audiobook-finished", onDone);
    return () => window.removeEventListener("audiobook-finished", onDone);
  }, [p]);
  return null;
};

/* Skip ±, speed and sleep timer — shown in the player bar / Now Playing while an audiobook plays. */
export const AudiobookControls = ({ tone = "light", compact = false }) => {
  const p = usePlayer();
  const dark = tone === "dark";
  const btn = `flex h-8 items-center justify-center rounded-full transition-colors ${dark ? "text-zinc-300 hover:bg-white/10 hover:text-white" : "text-zinc-600 hover:bg-zinc-200 hover:text-zinc-900"}`;
  return (
    <div className={`flex items-center ${compact ? "gap-0.5" : "gap-1"}`} data-testid="audiobook-controls">
      <button onClick={() => p.skipBy(-15)} title="Back 15 seconds" aria-label="Back 15 seconds" data-testid="audiobook-back-15" className={`${btn} relative w-8`}>
        <RotateCcw size={18} /><span className="absolute text-[7px] font-bold">15</span>
      </button>
      <button onClick={() => p.skipBy(30)} title="Forward 30 seconds" aria-label="Forward 30 seconds" data-testid="audiobook-fwd-30" className={`${btn} relative w-8`}>
        <RotateCw size={18} /><span className="absolute text-[7px] font-bold">30</span>
      </button>
      <BookmarkButton tone={tone} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button title="Playback speed" data-testid="audiobook-speed" className={`${btn} gap-1 px-2 font-mono text-[11px] font-semibold`}><Gauge size={14} />{p.rate}×</button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="min-w-[8rem]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Speed</DropdownMenuLabel>
          {SPEEDS.map((s) => (
            <DropdownMenuItem key={s} onSelect={() => p.setRate(s)} data-testid={`audiobook-speed-${String(s).replace(".", "_")}`} className={`font-mono text-xs ${p.rate === s ? "font-bold" : ""}`}>{s}×{p.rate === s ? " ✓" : ""}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button title="Sleep timer" data-testid="audiobook-sleep" className={`${btn} gap-1 px-2 text-[11px] ${p.sleep ? (dark ? "text-amber-300" : "text-amber-700") : ""}`} data-state-sleep={p.sleep ? "on" : "off"}>
            <Moon size={14} />{p.sleep?.until ? fmtLeft(p.sleep.until) : p.sleep?.chapter ? "ch." : null}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="min-w-[10rem]">
          <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-zinc-400">Sleep timer</DropdownMenuLabel>
          {SLEEPS.map((m) => <DropdownMenuItem key={m} onSelect={() => p.setSleep({ minutes: m })} data-testid={`audiobook-sleep-${m}`} className="text-xs">{m} minutes</DropdownMenuItem>)}
          <DropdownMenuItem onSelect={() => p.setSleep({ chapter: true })} data-testid="audiobook-sleep-chapter" className="text-xs">End of chapter</DropdownMenuItem>
          {p.sleep && <><DropdownMenuSeparator /><DropdownMenuItem onSelect={() => p.setSleep(null)} data-testid="audiobook-sleep-off" className="text-xs text-rose-600">Turn off</DropdownMenuItem></>}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
